/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BusinessSettingsModal} from '../business-settings-modal';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {LeadTimeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/lead-time.model';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {InputListModel} from '../input-list.model';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {ValidatorFn, Validators} from '@angular/forms';
import {combineLatest, Observable, of, timer} from 'rxjs';
import {BusinessSettingsApi} from '../../../../api/ms-settings/business/schema/v1/business-settings.api';
import {take} from 'rxjs/operators';
import {PeriodTypeConfigModel} from '../../../../api/ms-settings/business/schema/v1/contracts/period-type-config.model';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {InputDropdownComponent} from '../../../../shared/input/input-dropdown/input-dropdown.component';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';

@Component({
    selector: 'app-lead-time-modal',
    templateUrl: './lead-time-modal.component.html',
    styleUrls: ['../business-settings-modal.css', './lead-time-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeadTimeModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<LeadTimeModel>> {

    @ViewChild('quantity') quantityInput: InputTextComponent;
    @ViewChild('unit') unitInput: InputDropdownComponent;

    readonly ENTITY_NAME = 'lead time';
    readonly EDIT_TITLE = 'Edit lead time';
    readonly ADD_TITLE = 'Add a lead time';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';

    quantityInputMessages = new Map<WarningsEnum, string[]>();
    unitInputMessages = new Map<WarningsEnum, string[]>();

    periodTypeList: PeriodTypeConfigModel[] = [];

    validators: { [key: string]: ValidatorFn[] } = {
        quantity: [Validators.pattern(/^\d{1,3}$/)]
    };

    private readonly USED_COMBINATION_ERROR = `That ${this.ENTITY_NAME} is already in use`;

    constructor(public typeahead: TypeaheadHelperService,
                inputMessagesService: InputMessagesService,
                private settingsApi: BusinessSettingsApi) {
        super(inputMessagesService);
    }

    comparePeriodTypes(a: PeriodTypeConfigModel, b: PeriodTypeConfigModel): number {
        let comparison: number;

        if (a == null && b == null) {
            comparison = 0;
        } else if (a == null) {
            comparison = -1;
        } else if (b == null) {
            comparison = 1;
        } else {
            comparison = a.magnitude - b.magnitude;
        }

        return comparison;
    }

    getInitialData(): void {
        this.settingsApi.getAllPeriodTypes()
.pipe(take(1))
.subscribe((periodTypes) => {
            this.periodTypeList = periodTypes.sort(this.comparePeriodTypes);
        });
    }

    setEntity(element: EntityElementModel<LeadTimeModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: {...element.currentValue}
        });

        combineLatest([this.settingsApi.getAllPeriodTypes(), this.onReady])
                .pipe(take(1))
                .subscribe(([periodTypes, isReady]) => {
                    timer(0)
.subscribe(() => {
                        const unit = periodTypes.find((p) => p.code === this.state.currentValue.periodTypeCode);
                        this.periodTypeList = periodTypes.sort(this.comparePeriodTypes);
                        this.quantityInput.setValue(this.state.currentValue.value);
                        this.unitInput.setValue(unit);
                        this.setActiveInputState(element.active, true);
                    });
                });
    }

    searchUnit = (term: string): Observable<PeriodTypeConfigModel[]> => {
        if (term != null && term.length > 0) {
            return of(this.periodTypeList.filter((value) => `${value.name}`.toLowerCase()
.indexOf(term.toLowerCase()) > -1));
        }

        return of(this.periodTypeList);
    };

    mapEntity(): EntityElementModel<LeadTimeModel> {
        return {
            ...super.mapEntity(),
            name: `${this.state.currentValue.value}`,
            code: `${this.state.currentValue.value}-${this.state.currentValue.periodTypeCode}`
        };
    }

    quantityInputChange(quantity: string): void {
        const quantityNumber = Number.parseInt(quantity, 10);
        const quantityString = !Number.isNaN(quantityNumber) ? `${quantityNumber}` : quantity;

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                value: quantityString,
            }
        });

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: `${this.state.currentValue.value}-${this.state.currentValue.periodTypeCode}`
            }
        });

        this.updateCombinationError();
        this.inputMessages.mandatoryWarnings(this.quantityInput.inputControl, this.quantityInputMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.quantityInput.inputControl,
                this.quantityInputMessages,
                this.quantityInput.label,
                '059'
        );
        this.quantityInputMessages = new Map(this.quantityInputMessages);
        const errors = this.quantityInputMessages.get(WarningsEnum.ERROR);

        this.storeState({
            invalid: {
                ...this.state.invalid,
                value: (quantityString == null || quantityString.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    unitInputChange(unit: PeriodTypeConfigModel): void {
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                periodTypeCode: unit == null || unit.constructor === ''.constructor ? unit : unit.code
            }
        });

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: `${this.state.currentValue.value}-${this.state.currentValue.periodTypeCode}`
            }
        });

        this.updateCombinationError();
        this.inputMessages.mandatoryWarnings(this.unitInput.inputControl, this.unitInputMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(this.unitInput.inputControl, this.unitInputMessages, this.unitInput.label);
        this.unitInputMessages = new Map(this.unitInputMessages);
        const errors = this.unitInputMessages.get(WarningsEnum.ERROR);

        this.storeState({
            invalid: {
                ...this.state.invalid,
                periodTypeCode: unit == null || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.quantityInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.unitInput,
            type: InputTypeEnum.DROPDOWN,
            mandatory: true
        }];
    }

    // TODO: this function will be updated/removed in a later US (improvement US, not yet created on JIRA)
    private updateCombinationError(): void {
        let usedCodes = this.excludedEntities.map((e) => `${e.currentValue.value} ${e.currentValue.periodTypeCode}`);

        if (this.isEdit) {
            const original = `${this.state.originalElement.currentValue.value} ${this.state.originalElement.currentValue.periodTypeCode}`;
            usedCodes = usedCodes.filter((e) => e !== original);
        }

        if (usedCodes.includes(`${this.state.currentValue.value} ${this.state.currentValue.periodTypeCode}`)) {
            this.unitInputMessages.set(WarningsEnum.ERROR, [this.USED_COMBINATION_ERROR]);
            this.unitInputMessages = new Map(this.unitInputMessages);

            this.unitInput.markAsInvalid();
        } else if (this.periodTypeList.find((p) => p.code === this.state.currentValue.periodTypeCode) != null) {
            this.unitInputMessages.delete(WarningsEnum.ERROR);
            this.unitInputMessages = new Map(this.unitInputMessages);

            this.unitInput.markAsValid();
        }
    }
}
